import {Injectable} from "@angular/core";
import {DateRange} from "@angular/material/datepicker";
import moment from "moment";
import {Moment, unitOfTime} from "moment/moment";
import StartOf = moment.unitOfTime.StartOf;

@Injectable({
  providedIn: 'root'
})
export class DateUtilsService {

  constructor() {
    moment.locale('en-za');
  }

  displayOffsetDateTime(date: Date | string | number | null | Moment) {
    if (date) return moment(date).format("YYYY-MM-DD HH:mm:ss.SSS");
    return null;
  }

  displayShortDateUnix(date: number | any): string | null {
    if (date) return moment.unix(date).format("YYYY-MM-DD");
    return null;
  }

  displayShortDate(date: Date | string | number | null | Moment): string | null {
    if (date) {
      if (typeof date === 'number') {
        const reasonableMaxUnixSeconds = 4000000000;
        if (date < reasonableMaxUnixSeconds) {
          return moment.unix(date).format("YYYY-MM-DD");
        }
      }
      return moment(date).format("YYYY-MM-DD");
    }
    return null;
  }

  displayFullDate(date: Date | string | number | null): string | null {
    if (date) return moment(date).format("Do MMMM YYYY");
    return null;
  }

  displayMonthAndYear(date: Date | string | number | null | Moment): string | null {
    if (date) return moment(date).format("MMM YYYY");
    return null;
  }

  displayFullMonthAndYear(date: Date | string | number | null | Moment): string | null {
    if (date) return moment(date).format("MMMM YYYY");
    return null;
  }

  displayDateAndMonth(date: Date | string | number | null | Moment): string | null {
    if (date) return moment(date).format("DD MMM");
    return null;
  }

  displayDateWithTime(date: Date | string | null | Moment): string | null {
    if (date) return moment(date).format("YYYY-MM-DD (hh:mm)");
    return null;
  }

  displayDayAndDateWithTime(date: Date | string | null | Moment): string | null {
    if (date) return moment(date).format("ddd DD MMM hh:mm");
    return null;
  }

  displayYear(date: Date | string | number | null | Moment): string | null {
    if (date) return moment(date).format("YYYY");
    return null;
  }

  /**
   * ISO-8601 week helpers (Monday to Sunday). Note an ISO week may straddle a month boundary,
   * so a week is always represented by its Monday rather than by a month plus week-of-month.
   */
  startOfIsoWeek(date: Date | string | number | Moment): Date {
    return moment(date).startOf('isoWeek').toDate();
  }

  endOfIsoWeek(date: Date | string | number | Moment): Date {
    return moment(date).endOf('isoWeek').startOf('day').toDate();
  }

  isSameIsoWeek(a: Date | string | number | Moment | null, b: Date | string | number | Moment | null): boolean {
    if (!a || !b) return false;
    return moment(a).isSame(moment(b), 'isoWeek');
  }

  /** e.g. "Week 40, 2025" — uses the ISO week-numbering year, which can differ from the calendar year. */
  displayIsoWeek(date: Date | string | number | null | Moment): string | null {
    if (date) return `Week ${moment(date).isoWeek()}, ${moment(date).isoWeekYear()}`;
    return null;
  }

  /** e.g. "29 Sep - 5 Oct" — the span of the ISO week containing the given date. */
  displayIsoWeekRange(date: Date | string | number | null | Moment): string | null {
    if (!date) return null;
    const start = moment(date).startOf('isoWeek');
    const end = moment(date).endOf('isoWeek');
    return `${start.format("D MMM")} - ${end.format("D MMM")}`;
  }

  displayFullMonth(date: Date | string | number | null | Moment): string | null {
    if (date) return moment(date).format("MMMM");
    return null;
  }

  displayDateDuration(date1: string | Date, date2: string | Date) {
    const start = moment(date1);
    const end = moment(date2);

    const months = end.diff(start, 'months');
    const days = end.diff(start, 'days');

    return months > 0 ? `${months} month${months > 1 ? 's' : ''}` : `${days} day${days > 1 ? 's' : ''}`;
  }

  displayDateDurationDays(date: string | Date) {
    const days = moment(date).diff(moment(), 'days');
    return days > 0 ? moment(date).diff(moment(), 'days') + ' days' : '0 days';
  }

  getCurrentDate(): Date {
    return new Date();
  }

  isPastDated(date: string | Date): boolean {
    return moment(date).isBefore(new Date());
  }

  isFutureDated(date: string | Date): boolean {
    return moment(date).isAfter(new Date());
  }

  isPresentDated(date: string | Date): boolean {
    return moment(date).isSame(new Date(), "day");
  }

  getFirstDayOfMonth(date: string | Date): string {
    return moment(date).startOf('month').format('YYYY-MM-DD');
  }

  getFirstDayOfYear(date: string | Date): string {
    return moment(date).startOf('year').format('YYYY-MM-DD');
  }

  getLastDayOfMonth(date: string | Date): string {
    return moment(date).endOf('month').format('YYYY-MM-DD');
  }

  getNumberOfDaysInMonth(date: string | Date): number {
    return moment(date).daysInMonth();
  }

  getFirstDayOfQuarter(date: string | Date): string {
    return moment(date).startOf('quarter').format('YYYY-MM-DD');
  }

  getLastDayOfQuarter(date: string | Date): string {
    return moment(date).endOf('quarter').format('YYYY-MM-DD');
  }

  isWeekday(date: string | Date | Moment) {
    return !(moment(date).weekday() == 0 || moment(date).weekday() == 6);
  }

  doesOverlap(range1: DateRange<string | Date | null>, range2: DateRange<string | Date | null>) {
    return (range2.end == null || moment(range1.start).isSameOrBefore(moment(range2.end)))
      && (range1.end == null || moment(range1.end).isSameOrAfter(moment(range2.start)));
  }

  datesInclude(dates: Date[], date: Date) {
    let includes = false;
    dates.forEach(ea => {
      includes = moment(ea).isSame(date, 'day') || includes;
    })
  }

  inRange(date: string | Date, range: DateRange<string | Date>) {
    return moment(date).isSameOrBefore(moment(range.end)) && moment(date).isSameOrAfter(moment(range.start));
  }

  getDaysBetweenIncl(date1: string | Date, date2: string | Date | null) {
    if (!date2) {
      date2 = new Date();
    }
    return moment(date2).diff(moment(date1), 'days');
  }

  isBefore(date1: string | Date, date2: string | Date) {
    return moment(date1).isBefore(moment(date2));
  }

  isStartOfCurrentPeriod(date: string | Date | Moment, period: StartOf) {
    return moment(date).isSame(moment(new Date()).startOf(period), 'day');
  }

  getWeeksOfMonth(date: string | Date | Moment) {
    const weeks = [];

    const firstDay = moment(date).startOf('month');
    const lastDay = moment(date).endOf('month');
    let currentSunday = moment(firstDay).startOf('week');

    while (currentSunday.isSameOrBefore(lastDay)) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        if (currentSunday.isSameOrBefore(lastDay)) {
          if (currentSunday.month() == lastDay.month()) {
            week.push(currentSunday.clone());
          }
          currentSunday.add(1, 'day');
        }
      }
      weeks.push(week);
    }
    return weeks;
  }

  getNumberOfPeriods(date1: string | Date, date2: string | Date, period: unitOfTime.Diff) {
    const start = moment(date1);
    const end = moment(date2);
    return end.add(1, "day").diff(start, period, true);
  }

  getNumberOfWeekends(startDate: string | Date, endDate: string | Date, includeFridays: boolean = false) {
    const start = moment(startDate);
    const end = moment(endDate).add(1, "day");

    let count = 0;
    let current = start.clone();

    while (current < end) {
      const day = current.day(); // 0 = Sunday, 6 = Saturday, 5 = Friday
      if (includeFridays && (day === 0 || day === 6 || day == 5)) {
        count++;
      }

      if (!includeFridays && (day === 0 || day === 6)) {
        count++;
      }

      current.add(1, "day");
    }

    const weekendLength = includeFridays ? 3 : 2;

    return count/weekendLength;
  }

  add(date: string | Date, value: number, units: unitOfTime.DurationConstructor): Date | null {
    if (date) return moment(date).add(value, units).toDate();
    return null
  }
}
